import {
  SecondaryButton,
  SecondaryButtonIcon,
} from 'approot/shared/styles/buttons/buttons';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import SimpleModal from 'lib/modal/simple-modal';
import {
  SimpleModalContentStyled,
  SimpleModalVerticalActionsStyled,
} from 'lib/modal/simple-modal.styles';
import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import { PStyled } from 'approot/shared/styles/typography.style';
import InlineSvg from 'approot/shared/inline-svg/inline-svg';
import IconArrowLeft from 'approot/shared/icons/icon-arrow-left';
import {
  FlexLeftAlign,
  FlexRightAlign,
} from 'approot/shared/styles/flex.style';
import {
  PrimaryButton,
  PrimaryButtonIcon,
} from 'approot/shared/styles/buttons/primary-button.style';
import { Box } from 'approot/shared/design-system/box/box.style';
import * as Styled from './interactive-response-controls.style';
import IconQuestionMark from 'approot/shared/icons/icon-question-mark';
import IconAudioOff from 'approot/shared/icons/icon-audio-off';

export type InteractiveResponseControlsProps = {
  howToPlay: string | null;
  toggleAudio: () => void;
  playAudio: boolean;
};

export const InteractiveResponseHelpControls = ({
  howToPlay,
  toggleAudio,
  playAudio,
}: InteractiveResponseControlsProps) => {
  const { isVisible, openModal, closeModal } = useSimpleModalState();

  return (
    <Styled.InteractiveResponseControlsRight>
      {isVisible && (
        <SimpleModal
          isVisible
          title="How to play"
          headerTitle="How to Play"
          onClose={closeModal}
        >
          <SimpleModalContentStyled>
            <ScreenReaderOnly>
              <h2>Keyboard controls</h2>
              <PStyled>
                Press space bar to start a drag. When dragging you can use the
                right and left arrow keys to move the item between drop areas
                and escape to cancel. Press space bar again to drop an item.
                Some screen readers may require you to be in focus mode
              </PStyled>
            </ScreenReaderOnly>
            <PStyled marginBottom={0}>{howToPlay}</PStyled>
            <SimpleModalVerticalActionsStyled>
              <PrimaryButton type="button" onClick={() => closeModal()}>
                Got it
              </PrimaryButton>
            </SimpleModalVerticalActionsStyled>
          </SimpleModalContentStyled>
        </SimpleModal>
      )}
      <Styled.InteractiveResponseControls>
        <SecondaryButtonIcon
          data-selector="back-to-lesson-button"
          type="button"
          $borderRadius={40}
          aria-label="How to play - Read about keyboard controls and game rules"
          aria-haspopup
          onClick={openModal}
          style={{ visibility: howToPlay ? 'visible' : 'hidden' }}
          disabled={!howToPlay}
        >
          <IconQuestionMark width={24} height={24} />
        </SecondaryButtonIcon>
        <SecondaryButtonIcon
          type="button"
          $borderRadius={40}
          role="checkbox"
          aria-label={`Turn sound effects ${playAudio ? 'off' : 'on'}`}
          aria-checked={playAudio}
          onClick={toggleAudio}
        >
          {playAudio ? (
            <InlineSvg id="audio" width={24} height={24} />
          ) : (
            <IconAudioOff width={24} height={24} />
          )}
        </SecondaryButtonIcon>
      </Styled.InteractiveResponseControls>
    </Styled.InteractiveResponseControlsRight>
  );
};

export const InteractiveResponseStartAgainControls = ({
  onClick,
}: {
  onClick: () => void;
}) => {
  return (
    <Styled.InteractiveResponseControls>
      <SecondaryButton type="button" $borderRadius={40} onClick={onClick}>
        Start Over
      </SecondaryButton>
    </Styled.InteractiveResponseControls>
  );
};

export const InteractiveResponseNavControls = ({
  onPrevious,
  onNext,
  isNextDisabled,
  isPrevDisabled,
}: {
  onPrevious: () => void;
  onNext: () => void;
  isNextDisabled: boolean;
  isPrevDisabled: boolean;
}) => {
  return (
    <Box $gap={8} $justifyContent="center">
      <SecondaryButtonIcon
        type="button"
        $borderRadius={40}
        aria-label="Previous image"
        onClick={onPrevious}
        disabled={isPrevDisabled}
      >
        <IconArrowLeft width={20} height={20} />
      </SecondaryButtonIcon>
      <SecondaryButtonIcon
        type="button"
        $borderRadius={40}
        aria-label="Next image"
        onClick={onNext}
        disabled={isNextDisabled}
      >
        <IconArrowLeft rotate={180} width={20} height={20} />
      </SecondaryButtonIcon>
    </Box>
  );
};

export const InteractiveResponseNavMobileControls = ({
  onPrevious,
  onNext,
  onCheckAnswer,
  canCheckAnswer,
  isNextDisabled,
  isPrevDisabled,
  buttonLabel = 'Check Answer',
}: {
  onPrevious: () => void;
  onNext: () => void;
  onCheckAnswer: () => void;
  canCheckAnswer: boolean;
  isNextDisabled: boolean;
  isPrevDisabled: boolean;
  buttonLabel?: string;
}) => {
  return (
    <Styled.InteractiveResponseMobileNavControlsWrapper>
      <FlexLeftAlign>
        <SecondaryButtonIcon
          type="button"
          $borderRadius={40}
          aria-label="Previous image"
          onClick={onPrevious}
          disabled={isPrevDisabled}
        >
          <IconArrowLeft width={24} height={24} />
        </SecondaryButtonIcon>
      </FlexLeftAlign>
      <FlexRightAlign>
        {canCheckAnswer ? (
          <Styled.CheckAnswerButton type="button" onClick={onCheckAnswer}>
            {buttonLabel}
          </Styled.CheckAnswerButton>
        ) : (
          <PrimaryButtonIcon
            type="button"
            $borderRadius={40}
            aria-label="Next image"
            onClick={onNext}
            disabled={isNextDisabled}
          >
            <IconArrowLeft rotate={180} width={24} height={24} />
          </PrimaryButtonIcon>
        )}
      </FlexRightAlign>
    </Styled.InteractiveResponseMobileNavControlsWrapper>
  );
};
